import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { GetProps } from "tamagui";
import { styled, XStack, YStack } from "tamagui";
import type { TabLayout } from "@tamagui/tabs";
import { Tabs as TamaguiTabs, useTabsContext } from "@tamagui/tabs";
import { hairline } from "../../utils/hairline";
import { FadingScrollView } from "../FadingScrollView";
import { SelectionPillFrame, SelectionPillLabel } from "../SelectionPill";
import { Interface1 } from "../Typography";
const TabsBarFrame = styled(XStack, {
  name: "TabsBarFrame",
  px: "$sp4",
  width: "100%",
  borderBottomWidth: hairline,
  borderColor: "$borderDefault",
  flexDirection: "row",
  justifyContent: "flex-start",
  alignItems: "stretch",
  variants: {
    pills: {
      true: {
        px: 0,
        borderBottomWidth: 0
      }
    }
  } as const
});
const TabsBarTriggerFrame = styled(XStack, {
  name: "TabsBarTriggerFrame",
  cursor: "pointer",
  justifyContent: "center",
  alignItems: "center",
  paddingVertical: "$sp2.5",
  backgroundColor: "transparent",
  hoverStyle: {
    backgroundColor: "transparent"
  },
  focusVisibleStyle: {
    outlineWidth: "$si0.25",
    outlineColor: "$borderStrong",
    outlineStyle: "solid",
    outlineOffset: "$si0.25"
  }
});
const TabsBarTriggerText = styled(Interface1, {
  name: "TabsBarTriggerText",
  variants: {
    active: {
      true: {
        color: "$textDefault"
      },
      false: {
        color: "$textSubtle"
      }
    }
  } as const,
  defaultVariants: {
    active: false
  }
});
const TabsPillIndicator = styled(YStack, {
  name: "TabsPillIndicator",
  position: "absolute",
  top: 0,
  bottom: 0,
  borderRadius: "$brFull",
  backgroundColor: "$bgBrand",
  transition: "expand"
});

/** Animated underline indicator that slides between tabs. */
const TabsUnderline = styled(YStack, {
  name: "TabsUnderline",
  position: "absolute",
  bottom: 0,
  height: "$si0.25",
  backgroundColor: "$surfaceStrong",
  transition: "expand"
});
type TabsRootProps = GetProps<typeof TamaguiTabs>;
type TabsContentProps = GetProps<typeof TamaguiTabs.Content>;
export type TabsTab = {
  id: string;
  name: string;
};
export type TabsTabOptions = TabsTab[];
type TabsUnderlineStateContextValue = {
  activeLayout: TabLayout | null;
  setActiveLayout: (layout: TabLayout | null) => void;
  panels: number;
  setPanels: (update: (panels: number) => number) => void;
};
const TabsUnderlineStateContext = createContext<TabsUnderlineStateContextValue | null>(null);
const TabsRoot = (props: TabsRootProps) => {
  const [activeLayout, setActiveLayout] = useState<TabLayout | null>(null);
  const [panels, setPanels] = useState(0);
  const contextValue = useMemo(() => ({
    activeLayout,
    setActiveLayout,
    panels,
    setPanels
  }), [activeLayout, panels]);
  return <TabsUnderlineStateContext.Provider value={contextValue} data-sentry-element="TabsUnderlineStateContext.Provider" data-sentry-component="TabsRoot" data-sentry-source-file="Tabs.tsx">
      <TamaguiTabs flex={1} flexDirection="column" {...props} data-sentry-element="TamaguiTabs" data-sentry-source-file="Tabs.tsx" />
    </TabsUnderlineStateContext.Provider>;
};
type TabsBarProps = Omit<GetProps<typeof TabsBarFrame>, "pills"> & {
  tabs: TabsTabOptions;
  variant?: "underline" | "pills";
};
const TabsBar = ({
  tabs,
  variant = "underline",
  ...rest
}: TabsBarProps) => {
  const pills = variant === "pills";
  const {
    value
  } = useTabsContext();
  const underlineState = useContext(TabsUnderlineStateContext);
  const activeLayout = tabs.some(tab => tab.id === value) ? underlineState?.activeLayout ?? null : null;
  // Tamagui points every tab at a panel id; with no panels, point at none.
  const panelProps = (underlineState?.panels ?? 0) > 0 ? {} : {
    "aria-controls": undefined
  };
  const handleInteraction = useCallback((type: "hover" | "focus" | "select", layout: TabLayout | null) => {
    if (type === "select" && underlineState?.setActiveLayout) {
      underlineState.setActiveLayout(layout);
    }
  }, [underlineState?.setActiveLayout]);
  return <TabsBarFrame pills={pills} {...rest} data-sentry-element="TabsBarFrame" data-sentry-component="TabsBar" data-sentry-source-file="Tabs.tsx">
      <TamaguiTabs.List unstyled flex={1} data-sentry-element="TamaguiTabs.List" data-sentry-source-file="Tabs.tsx">
        <FadingScrollView axis="horizontal"
      // Bars sit on `$surface1`; fades stop above the underline.
      background="$surface1" fadeInset={pills ? 0 : "$si0.25"} flex={1} showsHorizontalScrollIndicator={false} revealRange={activeLayout ? {
        start: activeLayout.x,
        end: activeLayout.x + activeLayout.width
      } : null} data-sentry-element="FadingScrollView" data-sentry-source-file="Tabs.tsx">
          <XStack position="relative" flexDirection="row" alignItems="stretch" gap={pills ? "$sp0.25" : "$sp6"} data-sentry-element="XStack" data-sentry-source-file="Tabs.tsx">
            {pills && activeLayout ? <TabsPillIndicator x={activeLayout.x} width={activeLayout.width} /> : null}
            {tabs.map(tab => <TamaguiTabs.Tab key={tab.id} value={tab.id} unstyled onInteraction={handleInteraction} {...panelProps}>
                {pills ? <SelectionPillFrame zIndex={1}>
                    <SelectionPillLabel look={value === tab.id ? "brandOn" : "off"}>
                      {tab.name}
                    </SelectionPillLabel>
                  </SelectionPillFrame> : <TabsBarTriggerFrame>
                    <TabsBarTriggerText active={value === tab.id}>
                      {tab.name}
                    </TabsBarTriggerText>
                  </TabsBarTriggerFrame>}
              </TamaguiTabs.Tab>)}
            {!pills && activeLayout ? <TabsUnderline x={activeLayout.x} width={activeLayout.width} /> : null}
          </XStack>
        </FadingScrollView>
      </TamaguiTabs.List>
    </TabsBarFrame>;
};
const TabsContent = (props: TabsContentProps) => {
  const setPanels = useContext(TabsUnderlineStateContext)?.setPanels;
  useEffect(() => {
    setPanels?.(panels => panels + 1);
    return () => setPanels?.(panels => panels - 1);
  }, [setPanels]);
  return <TamaguiTabs.Content flex={1} {...props} data-sentry-element="TamaguiTabs.Content" data-sentry-component="TabsContent" data-sentry-source-file="Tabs.tsx" />;
};
type TabsComposition = {
  Bar: typeof TabsBar;
  Content: typeof TabsContent;
};
export const Tabs = Object.assign(TabsRoot, {
  Bar: TabsBar,
  Content: TabsContent
}) as typeof TabsRoot & TabsComposition;
export type { TabsBarProps, TabsContentProps, TabsRootProps };